"use client";

import React, { useState, useCallback } from "react";
import { MODAL_IDS, ModalContainer, useUniversalAuth } from "@west-tennessee-consulting/ui";
import { UnifiedModal } from "@/components/shared/modals";
import { Button } from "@/components/ui/button";
import {
  EnhancedTabs,
  EnhancedTabsList,
  EnhancedTabsTrigger,
  EnhancedTabsContent,
} from "@/components/ui/tabs/enhanced-tabs";
import { MessageSquare, CheckSquare, User, AlertCircle } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useCommunicationTypes } from "@/lib/hooks/api/communications/communication-types";
import type { LogActivityModalData } from "@/lib/modals/data/modal-data-types";

import { CommunicationForm, TaskForm } from "./activity-forms";
import { useLogActivityContact } from "./useLogActivityContact";
import { useLogActivitySubmit } from "./useLogActivitySubmit";

export type ActivityType = "communication" | "task";

const ACTIVITY_TYPES = [
  {
    value: "communication" as const,
    label: "Communication",
    icon: MessageSquare,
    description: "Log calls, emails, and other communications with detailed tracking",
  },
  {
    value: "task" as const,
    label: "Task",
    icon: CheckSquare,
    description: "Create and assign tasks with due dates and priority levels",
  },
];

/**
 * Log Activity Modal
 *
 * Uses global modal stack with ID "log-activity".
 * Consolidates communication and task logging.
 * Contact resolution → useLogActivityContact
 * Submit logic → useLogActivitySubmit
 */
export const LogActivityModal: React.FC = () => {
  const { appUser } = useUniversalAuth();
  const [activeTab, setActiveTab] = useState<ActivityType>("communication");
  const { options: typeOptions, loading: isLoadingTypes } = useCommunicationTypes();

  const { effectiveContactId, contact, isLoadingContact } = useLogActivityContact();
  const userId = appUser?.id;
  const { handleCommunicationSubmit, handleTaskSubmit, isLoading } = useLogActivitySubmit(
    effectiveContactId,
    userId
  );

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as ActivityType);
  }, []);

  return (
    <ModalContainer<LogActivityModalData> modalId={MODAL_IDS.messages.logActivity}>
      {({ onClose }: { isOpen: boolean; onClose: () => void }) => {
        if (!effectiveContactId || !userId) {
          return (
            <UnifiedModal
              modalId={MODAL_IDS.messages.logActivity}
              title="Log Activity"
              description="Unable to log activity"
              size="lg"
            >
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500">
                  {!effectiveContactId ? "No contact found. " : ""}
                  {!userId ? "No user selected. " : ""}
                  Please try again.
                </p>
              </div>
            </UnifiedModal>
          );
        }

        return (
          <UnifiedModal
            modalId={MODAL_IDS.messages.logActivity}
            title="Log Activity"
            description="Record communications and tasks for this contact"
            size="lg"
          >
            <div className="space-y-6">
              {/* Contact Header */}
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                {isLoadingContact ? (
                  <>
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </>
                ) : contact ? (
                  <>
                    <Avatar className="h-10 w-10 flex-shrink-0">
                      <AvatarFallback className="bg-blue-200 text-blue-900 font-semibold dark:bg-blue-900/50 dark:text-blue-200">
                        {contact.name
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("")
                          .toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-card-foreground truncate">{contact.name}</p>
                      <p className="text-xs text-gray-600 truncate dark:text-gray-400">
                        {contact.email || contact.phone || "No contact info"}
                      </p>
                    </div>
                    <User className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <AlertCircle className="h-4 w-4" />
                    <span>Contact not found</span>
                  </div>
                )}
              </div>

              {/* Activity Type Tabs */}
              <EnhancedTabs value={activeTab} onValueChange={handleTabChange}>
                <EnhancedTabsList className="grid w-full grid-cols-2">
                  {ACTIVITY_TYPES.map((type, index) => {
                    const Icon = type.icon;
                    const variants = ["blue", "green"] as const;
                    return (
                      <EnhancedTabsTrigger
                        key={type.value}
                        value={type.value}
                        variant={variants[index]}
                        className="flex items-center gap-2 text-xs"
                      >
                        <Icon size={16} />
                        <span className="hidden sm:inline">{type.label}</span>
                      </EnhancedTabsTrigger>
                    );
                  })}
                </EnhancedTabsList>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    {ACTIVITY_TYPES.find((t) => t.value === activeTab)?.description}
                  </p>
                </div>

                <EnhancedTabsContent value="communication" className="space-y-4">
                  <CommunicationForm
                    contactId={effectiveContactId}
                    userId={userId}
                    onSubmit={handleCommunicationSubmit}
                    isSubmitting={isLoading}
                    typeOptions={typeOptions}
                    isLoadingTypes={isLoadingTypes}
                  />
                </EnhancedTabsContent>

                <EnhancedTabsContent value="task" className="space-y-4">
                  <TaskForm onSubmit={handleTaskSubmit} isSubmitting={isLoading} />
                </EnhancedTabsContent>
              </EnhancedTabs>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
              </div>
            </div>
          </UnifiedModal>
        );
      }}
    </ModalContainer>
  );
};
